import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';

export type PanelName = 'overview' | 'breadcrumbs' | 'performance' | 'spatial' | 'objects' | 'network';

@Component({
  selector: 'app-session-panel-toolbar',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="absolute top-1/2 right-0 -translate-y-1/2 z-10 flex flex-col gap-2 pr-3">
      <!-- Overview -->
      <button (click)="panelToggled.emit('overview')"
              class="w-10 h-10 rounded-none flex items-center justify-center transition-all text-sm group relative"
              [class]="activePanel === 'overview' ? 'bg-zaxis-accent text-white shadow-pixel shadow-zaxis-accent/40' : 'bg-white/10 text-gray-400 hover:bg-white/20 hover:text-white border border-zaxis-border'">
        <i class="ph ph-clipboard-text text-xl"></i>
        <span class="tooltip-label flex flex-col gap-0.5 text-left">
          <strong class="font-bold">Overview</strong>
          <span class="text-[9px] text-gray-400 font-normal whitespace-normal w-44 leading-tight">Información general de la sesión, dispositivo y contexto XR.</span>
        </span>
      </button>

      <!-- Breadcrumbs -->
      <button (click)="panelToggled.emit('breadcrumbs')"
              class="w-10 h-10 rounded-none flex items-center justify-center transition-all text-sm relative group"
              [class]="activePanel === 'breadcrumbs' ? 'bg-zaxis-accent text-white shadow-pixel shadow-zaxis-accent/40' : 'bg-white/10 text-gray-400 hover:bg-white/20 hover:text-white border border-zaxis-border'">
        <i class="ph ph-compass text-xl"></i>
        <span *ngIf="breadcrumbCount" class="absolute -top-1 -right-1 w-4 h-4 bg-zaxis-accent rounded-none text-[9px] font-bold flex items-center justify-center text-white">
          {{ breadcrumbCount > 9 ? '9+' : breadcrumbCount }}
        </span>
        <span class="tooltip-label flex flex-col gap-0.5 text-left">
          <strong class="font-bold">Breadcrumbs</strong>
          <span class="text-[9px] text-gray-400 font-normal whitespace-normal w-44 leading-tight">Registro cronológico de acciones, logs y eventos del usuario.</span>
        </span>
      </button>

      <!-- Performance -->
      <button (click)="panelToggled.emit('performance')"
              class="w-10 h-10 rounded-none flex items-center justify-center transition-all text-sm group relative"
              [class]="activePanel === 'performance' ? 'bg-zaxis-accent text-white shadow-pixel shadow-zaxis-accent/40' : 'bg-white/10 text-gray-400 hover:bg-white/20 hover:text-white border border-zaxis-border'">
        <i class="ph ph-lightning text-xl"></i>
        <span class="tooltip-label flex flex-col gap-0.5 text-left">
          <strong class="font-bold">Rendimiento</strong>
          <span class="text-[9px] text-gray-400 font-normal whitespace-normal w-44 leading-tight">Métricas de FPS, uso de CPU/GPU y frecuencia de refresco.</span>
        </span>
      </button>

      <!-- Spatial -->
      <button (click)="panelToggled.emit('spatial')"
              class="w-10 h-10 rounded-none flex items-center justify-center transition-all text-sm group relative"
              [class]="activePanel === 'spatial' ? 'bg-zaxis-accent text-white shadow-pixel shadow-zaxis-accent/40' : 'bg-white/10 text-gray-400 hover:bg-white/20 hover:text-white border border-zaxis-border'">
        <i class="ph ph-target text-xl"></i>
        <span class="tooltip-label flex flex-col gap-0.5 text-left">
          <strong class="font-bold">Spatial Target</strong>
          <span class="text-[9px] text-gray-400 font-normal whitespace-normal w-44 leading-tight">Último objeto 3D con el que el usuario interactuó y su distancia.</span>
        </span>
      </button>

      <!-- Network -->
      <button (click)="panelToggled.emit('network')"
              class="w-10 h-10 rounded-none flex items-center justify-center transition-all text-sm group relative"
              [class]="activePanel === 'network' ? 'bg-zaxis-accent text-white shadow-pixel shadow-zaxis-accent/40' : 'bg-white/10 text-gray-400 hover:bg-white/20 hover:text-white border border-zaxis-border'">
        <i class="ph ph-globe text-xl"></i>
        <span class="tooltip-label flex flex-col gap-0.5 text-left">
          <strong class="font-bold">API / Network</strong>
          <span class="text-[9px] text-gray-400 font-normal whitespace-normal w-44 leading-tight">Métricas de consumos web, latencia de APIs y clasificación Apdex.</span>
        </span>
      </button>

      <!-- Objects -->
      <button (click)="panelToggled.emit('objects')"
              class="w-10 h-10 rounded-none flex items-center justify-center transition-all text-sm group relative"
              [class]="activePanel === 'objects' ? 'bg-zaxis-accent text-white shadow-pixel shadow-zaxis-accent/40' : 'bg-white/10 text-gray-400 hover:bg-white/20 hover:text-white border border-zaxis-border'">
        <i class="ph ph-package text-xl"></i>
        <span class="tooltip-label flex flex-col gap-0.5 text-left">
          <strong class="font-bold">Heatmaps por Objeto</strong>
          <span class="text-[9px] text-gray-400 font-normal whitespace-normal w-44 leading-tight">Lista de objetos en la escena.</span>
        </span>
      </button>

      <div class="w-6 border-t border-zaxis-border mx-auto my-1"></div>

      <!-- Scene toggle -->
      <button (click)="sceneToggled.emit()"
              class="w-10 h-10 rounded-none flex items-center justify-center transition-all text-sm group relative"
              [class]="showScene ? 'bg-emerald-500/80 text-white shadow-pixel shadow-emerald-500/30' : 'bg-white/10 text-gray-400 hover:bg-white/20 hover:text-white border border-zaxis-border'">
        <i class="ph ph-buildings text-xl"></i>
        <span class="tooltip-label flex flex-col gap-0.5 text-left">
          <strong class="font-bold">Escena</strong>
          <span class="text-[9px] text-gray-400 font-normal whitespace-normal w-44 leading-tight">Mostrar u ocultar la representación wireframe del entorno 3D.</span>
        </span>
      </button>

      <!-- Gaze trail toggle -->
      <button (click)="gazeToggled.emit()"
              class="w-10 h-10 rounded-none flex items-center justify-center transition-all text-sm group relative"
              [class]="showGazeTrail ? 'bg-orange-500/80 text-white shadow-pixel shadow-orange-500/30' : 'bg-white/10 text-gray-400 hover:bg-white/20 hover:text-white border border-zaxis-border'">
        <i class="ph ph-eye text-xl"></i>
        <span class="tooltip-label flex flex-col gap-0.5 text-left">
          <strong class="font-bold">Gaze Trail</strong>
          <span class="text-[9px] text-gray-400 font-normal whitespace-normal w-44 leading-tight">Mostrar u ocultar el rastro de los puntos de mirada (heatmap).</span>
        </span>
      </button>

      <!-- Recenter camera -->
      <button (click)="cameraReset.emit()"
              class="w-10 h-10 rounded-none flex items-center justify-center transition-all text-sm group relative bg-white/10 text-gray-400 hover:bg-white/20 hover:text-white border border-zaxis-border">
        <i class="ph ph-arrows-clockwise text-xl"></i>
        <span class="tooltip-label flex flex-col gap-0.5 text-left">
          <strong class="font-bold">Centrar Cámara</strong>
          <span class="text-[9px] text-gray-400 font-normal whitespace-normal w-44 leading-tight">Vuelve a centrar la vista en el origen de la escena.</span>
        </span>
      </button>
    </div>
  `,
  styles: [`
    .tooltip-label {
      position: absolute;
      right: calc(100% + 10px);
      top: 50%;
      transform: translateY(-50%);
      padding: 6px 12px;
      background: rgba(0,0,0,0.85);
      backdrop-filter: blur(8px);
      border: 1px solid rgba(255,255,255,0.1);
      border-radius: 8px;
      font-size: 11px;
      color: #e2e8f0;
      pointer-events: none;
      opacity: 0;
      transition: opacity 0.15s ease;
      z-index: 50;
    }
    .group:hover .tooltip-label { opacity: 1; }
  `]
})
export class SessionPanelToolbarComponent {
  @Input() activePanel: string | null = null;
  @Input() showScene = true;
  @Input() showGazeTrail = true;
  @Input() breadcrumbCount = 0;

  @Output() panelToggled = new EventEmitter<PanelName>();
  @Output() sceneToggled = new EventEmitter<void>();
  @Output() gazeToggled = new EventEmitter<void>();
  @Output() cameraReset = new EventEmitter<void>();
}
